import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ExternalLink, Copy, CheckCircle, Globe, MessageSquare, BookOpen, Pen, Search, Users } from 'lucide-react';

const PLATFORMS = [
  {
    category: 'Reddit',
    icon: MessageSquare,
    color: 'text-orange-400',
    sites: [
      { name: 'r/selfpublish', url: 'https://reddit.com/r/selfpublish', members: '130k+', tip: 'Share value-first posts about review strategies. No direct ads — offer tips, mention The Exchange naturally.' },
      { name: 'r/writing', url: 'https://reddit.com/r/writing', members: '3M+', tip: 'Focus on writing craft discussions. Link to your blog posts about book marketing.' },
      { name: 'r/KindleDirectPublishing', url: 'https://reddit.com/r/KindleDirectPublishing', members: '20k+', tip: 'Directly relevant. Share ARC launch tips and mention The Exchange as a resource.' },
      { name: 'r/ebooks', url: 'https://reddit.com/r/ebooks', members: '150k+', tip: 'Great for reaching readers. Promote free ARC copies available on The Exchange.' },
      { name: 'r/bookmarketing', url: 'https://reddit.com/r/bookmarketing', members: '5k+', tip: 'Highly targeted. Share review strategy content.' },
    ]
  },
  {
    category: 'Author Forums',
    icon: Pen,
    color: 'text-blue-400',
    sites: [
      { name: 'KBoards', url: 'https://www.kboards.com/forums/', members: 'Top KDP forum', tip: 'THE community for Kindle authors. Post in the Writers Cafe section about review strategies.' },
      { name: 'AbsoluteWrite', url: 'https://absolutewrite.com/forum/', members: 'Legacy forum', tip: 'Respected writing community. Share in Self-Publishing section.' },
      { name: 'Scribophile', url: 'https://www.scribophile.com/', members: 'Craft-focused', tip: 'Writing critique community. Share blog links in appropriate forums.' },
      { name: 'NaNoWriMo Forums', url: 'https://forums.nanowrimo.org/', members: 'Seasonal surge', tip: 'Active during Nov. Great for reaching first-time authors who need launch help.' },
      { name: 'WritersCafe', url: 'https://www.writerscafe.org/', members: 'Open community', tip: 'Post reviews and writing content. Link to Exchange in your profile.' },
    ]
  },
  {
    category: 'Publishing Communities',
    icon: BookOpen,
    color: 'text-green-400',
    sites: [
      { name: 'ALLi (Alliance of Independent Authors)', url: 'https://www.allianceindependentauthors.org/', members: 'Professional', tip: 'Paid membership but influential. Contribute to their blog or forum discussions.' },
      { name: 'Reedsy Community', url: 'https://blog.reedsy.com/', members: 'Large reach', tip: 'Comment on blog posts about book marketing. Share valuable insights with link.' },
      { name: 'Goodreads Author Groups', url: 'https://www.goodreads.com/group', members: 'Massive', tip: 'Join author groups. Discuss review strategies. Link to your blog posts.' },
      { name: 'Draft2Digital Community', url: 'https://draft2digital.com/blog/', members: 'Wide distro users', tip: 'Authors who distribute wide. Comment with valuable insights.' },
      { name: 'BookFunnel Authors', url: 'https://bookfunnel.com/', members: 'ARC-savvy', tip: 'Authors already using ARC tools. Mention The Exchange as a free alternative.' },
    ]
  },
  {
    category: 'Content Platforms',
    icon: Globe,
    color: 'text-purple-400',
    sites: [
      { name: 'Medium', url: 'https://medium.com/', members: 'Massive reach', tip: 'Write articles like "How I Got 50 Reviews in My First Month." Tag: self-publishing, book-marketing.' },
      { name: 'Quora', url: 'https://www.quora.com/', members: 'SEO gold', tip: 'Answer questions about getting book reviews, ARC strategies. Link to The Exchange naturally.' },
      { name: 'LinkedIn Articles', url: 'https://www.linkedin.com/pulse/', members: 'Professional', tip: 'Publish thought leadership about author entrepreneurship. Link to Exchange.' },
      { name: 'Substack', url: 'https://substack.com/', members: 'Newsletter', tip: 'Start a publishing tips newsletter. Funnel subscribers to The Exchange.' },
      { name: 'Vocal.media', url: 'https://vocal.media/', members: 'Creators', tip: 'Write about indie publishing. Gets good Google indexing.' },
    ]
  },
  {
    category: 'Q&A & Search',
    icon: Search,
    color: 'text-cyan-400',
    sites: [
      { name: 'Quora Spaces', url: 'https://www.quora.com/spaces', members: 'Topic hubs', tip: 'Create or join a Self-Publishing space. Post regular tips.' },
      { name: 'Product Hunt', url: 'https://www.producthunt.com/', members: 'Tech-savvy', tip: 'Launch The Exchange as a product. Great for initial visibility boost.' },
      { name: 'Indie Hackers', url: 'https://www.indiehackers.com/', members: 'Builders', tip: 'Share the story of building The Exchange. Indie authors hang here too.' },
      { name: 'Hacker News', url: 'https://news.ycombinator.com/', members: 'Tech crowd', tip: 'Show HN post about The Exchange. Technical audience but wide reach.' },
    ]
  },
  {
    category: 'Facebook Groups (Post Manually)',
    icon: Users,
    color: 'text-blue-500',
    sites: [
      { name: '20BooksTo50K', url: 'https://facebook.com/groups/20booksto50k', members: '50k+', tip: 'THE indie author group. Share value posts about review strategies. No hard selling.' },
      { name: 'Self-Publishing with Dale', url: 'https://facebook.com/groups/selfpublishingwitdale', members: 'Very active', tip: 'Supportive community. Share wins and tips.' },
      { name: 'KDP Kindle Direct Publishing', url: 'https://facebook.com/groups/kindledirectpublishing', members: 'KDP focused', tip: 'Directly relevant. Share ARC and review tips.' },
      { name: 'Indie Author Support Group', url: 'https://facebook.com/groups/indieauthorsupport', members: 'Supportive', tip: 'Great for engagement posts. Ask questions, share tips.' },
      { name: 'Book Marketing for Authors', url: 'https://facebook.com/groups/bookmarketingforauthors', members: 'Marketing focus', tip: 'Share your review strategy content. Link to blog posts.' },
      { name: 'Self Published Authors', url: 'https://facebook.com/groups/selfpublishedauthors', members: 'Large', tip: 'General self-pub community. Share useful content regularly.' },
    ]
  },
];

const POST_TEMPLATES = [
  {
    platform: 'Reddit / Forums',
    title: 'Value Post (Soft Pitch)',
    template: `I've been self-publishing for a while and the biggest challenge has always been getting those first reviews. Here's what's been working for me recently:

1. Build an ARC team before launch day — have 15-25 readers ready
2. Use a credit-based review community where you review others' books to earn credits, then use credits to get reviews on yours
3. Always email your existing readers 2 weeks after purchase with a gentle review request

The credit-based approach has been a game-changer because it's 100% Amazon-compliant and creates a sustainable cycle.

I've been using The Authors' Exchange on UPost.com — it's free to join and you start with 50 credits. Happy to share more details if anyone's interested.`
  },
  {
    platform: 'Medium / LinkedIn',
    title: 'Thought Leadership Article Intro',
    template: `How I Got 30 Honest Reviews in My First Month as a Self-Published Author

When I launched my first book, I had exactly zero reviews. Three months in, I still had zero reviews. Sound familiar?

The truth is, most self-published authors struggle not because their books are bad, but because they don't have a review strategy.

Here's what changed everything for me: I joined a credit-based review exchange called The Authors' Exchange. Instead of begging friends and family, I reviewed other authors' books (which I actually enjoyed), earned credits, and used those credits to get honest reviews on my own book.

Within 30 days, I had 30 verified Amazon reviews. Here's exactly how...

[Continue with your story and link to upost.com/club]`
  },
  {
    platform: 'Facebook Groups',
    title: 'Engagement Post',
    template: `Quick question for fellow authors: What's your biggest challenge with getting Amazon reviews?

For me, it was finding REAL readers who would actually follow through. I tried Facebook ARC groups, BookSprout, even cold-emailing book bloggers — nothing consistent.

Recently discovered a free credit-based system where you review books to earn credits, then use credits to get reviews. No direct swaps (which Amazon hates), just a community helping each other.

Anyone else tried something like this? What's worked for you?`
  },
  {
    platform: 'Quora',
    title: 'Answer Template',
    template: `Great question! Getting Amazon reviews is the #1 challenge for self-published authors. Here are strategies that actually work in 2026:

1. **Join a review exchange community** — The Authors' Exchange (upost.com/club) is free and uses a credit system. You review other books, earn credits, and use them to get reviews on yours. It's Amazon-compliant because there are no direct swaps.

2. **Build an ARC team** — Distribute free advance copies before launch. The same platform has a built-in ARC launch system.

3. **Email your readers** — After someone buys your book, follow up with a gentle review request 2-3 weeks later.

4. **Never buy reviews** — Amazon will ban your account.

The credit-based approach is the most sustainable because it creates a community where everyone benefits.`
  },
];

export default function GrowthHub() {
  const [copied, setCopied] = useState(null);
  const [expandedCat, setExpandedCat] = useState('Reddit');

  const copyTemplate = (idx) => {
    navigator.clipboard.writeText(POST_TEMPLATES[idx].template);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0a0f]">
        <section className="py-10 px-4 border-b border-gray-800">
          <div className="container mx-auto max-w-5xl">
            <h1 className="text-3xl font-bold text-white mb-2" data-testid="growth-hub-title">Growth Hub</h1>
            <p className="text-gray-400">Your outreach command center — post to these platforms to drive signups to The Authors' Exchange</p>
          </div>
        </section>

        <div className="container mx-auto max-w-5xl py-8 px-4">
          {/* Platform Directory */}
          <h2 className="text-lg font-semibold text-white mb-4">Where to Post</h2>
          <div className="space-y-3 mb-12">
            {PLATFORMS.map(cat => {
              const isExpanded = expandedCat === cat.category;
              return (
                <Card key={cat.category} className="bg-gray-900/50 border-gray-800">
                  <button
                    onClick={() => setExpandedCat(isExpanded ? null : cat.category)}
                    className="w-full p-4 flex items-center justify-between text-left"
                    data-testid={`growth-cat-${cat.category.replace(/[^a-zA-Z]/g, '')}`}
                  >
                    <div className="flex items-center gap-3">
                      <cat.icon className={`w-5 h-5 ${cat.color}`} />
                      <span className="text-white font-semibold">{cat.category}</span>
                      <span className="text-xs text-gray-500">{cat.sites.length} platforms</span>
                    </div>
                    <span className="text-gray-500 text-sm">{isExpanded ? '−' : '+'}</span>
                  </button>
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 px-4">
                      <div className="space-y-3">
                        {cat.sites.map((site, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <a href={site.url} target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:text-amber-400 flex items-center gap-1">
                                  {site.name} <ExternalLink className="w-3 h-3" />
                                </a>
                                <span className="text-xs text-gray-500">{site.members}</span>
                              </div>
                              <p className="text-xs text-gray-400">{site.tip}</p>
                            </div>
                            <a href={site.url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" variant="outline" className="border-gray-700 text-gray-300 hover:border-amber-400 hover:text-amber-400 text-xs shrink-0">
                                Go Post
                              </Button>
                            </a>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Post Templates */}
          <h2 className="text-lg font-semibold text-white mb-4">Copy-Paste Templates</h2>
          <div className="space-y-4">
            {POST_TEMPLATES.map((t, i) => (
              <Card key={i} className="bg-gray-900/50 border-gray-800" data-testid={`growth-template-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{t.platform}</span>
                      <h3 className="text-white font-semibold mt-1">{t.title}</h3>
                    </div>
                    <Button
                      onClick={() => copyTemplate(i)}
                      size="sm"
                      variant="outline"
                      className={`border-gray-700 text-xs shrink-0 ${copied === i ? 'border-green-500 text-green-400' : 'text-gray-300 hover:border-amber-400 hover:text-amber-400'}`}
                      data-testid={`copy-template-${i}`}
                    >
                      {copied === i ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                    </Button>
                  </div>
                  <pre className="text-sm text-gray-400 whitespace-pre-wrap bg-gray-800/50 rounded p-4 max-h-48 overflow-y-auto font-sans">{t.template}</pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
